import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Person } from '../../types';
import type {
  CandidateCertificationEntry,
  CertificationInput,
} from '../../types/candidateDetails';
// @ts-ignore
import { peopleApi } from '../../services/api';
import { useFocusTrap } from '../../hooks/useFocusTrap';

/**
 * Phase 3E-3 (Commit 3 — 3B) — full-screen editor for an external
 * candidate's certifications collection.
 *
 * Atomic PUT /api/people/:id/certifications replaces the `admin_edit`
 * shadow layer only; the `cv` layer stays intact.
 * `projection_locks.certifications = true` is stamped server-side so
 * CV re-imports leave manual edits alone.
 *
 * UX:
 *   - Entry reorder via native HTML5 drag-and-drop.
 *   - Unsaved-changes guard on close / Cancel / Escape.
 *   - Field-level 400 / 413 errors parsed from dotted path
 *     `certifications[i].field` and highlighted on the offending row;
 *     cap errors with `field = "certifications"` surface as a global
 *     banner.
 *   - `name` is required client-side (mirrors the server validation)
 *     so the user sees a local error before hitting Save.
 *
 * Wireframe: docs/designs/candidate-edit-flows.svg (section G).
 */

import { adminProfileApi, type ProfileMutationsApi } from '../../services/profileMutationsApi';

export interface CertificationsEditorModalProps {
  person: Person;
  apiClient?: ProfileMutationsApi;
  onClose: () => void;
  onSaved: (updated: Person) => void;
}

const DRAG_TYPE = 'application/x-certifications-reorder';

interface EntryForm {
  localId: string;
  name: string;
  issuer: string;
  issued_date: string;
  expires_date: string;
  credential_id: string;
  credential_url: string;
}

let _localIdCounter = 0;
function nextLocalId(): string {
  _localIdCounter += 1;
  return `cert-${_localIdCounter}-${Date.now().toString(36)}`;
}

function entryFromCandidate(c: CandidateCertificationEntry): EntryForm {
  return {
    localId: nextLocalId(),
    name: c.name ?? '',
    issuer: c.issuer ?? '',
    issued_date: c.issuedDate ?? '',
    expires_date: c.expiresDate ?? '',
    credential_id: c.credentialId ?? '',
    credential_url: c.credentialUrl ?? '',
  };
}

function blankEntry(): EntryForm {
  return {
    localId: nextLocalId(),
    name: '',
    issuer: '',
    issued_date: '',
    expires_date: '',
    credential_id: '',
    credential_url: '',
  };
}

function nullIfEmpty(s: string): string | null {
  const t = s.trim();
  return t === '' ? null : t;
}

function entryToPayload(e: EntryForm): CertificationInput {
  return {
    name: e.name.trim(),
    issuer: nullIfEmpty(e.issuer),
    issued_date: nullIfEmpty(e.issued_date),
    expires_date: nullIfEmpty(e.expires_date),
    credential_id: nullIfEmpty(e.credential_id),
    credential_url: nullIfEmpty(e.credential_url),
  };
}

interface ParsedError {
  index: number | null;
  leaf: string;
}

function parseErrorField(field: string | undefined): ParsedError | null {
  if (!field) return null;
  const m = /^certifications\[(\d+)\]\.?(.*)$/.exec(field);
  if (!m) return { index: null, leaf: field };
  return { index: Number(m[1]), leaf: m[2] ?? '' };
}

export const CertificationsEditorModal: React.FC<CertificationsEditorModalProps> = ({
  person,
  apiClient = adminProfileApi,
  onClose,
  onSaved,
}) => {
  const initialEntries = useMemo<EntryForm[]>(() => {
    const src = person.CandidateDetails?.certifications ?? [];
    return src.map(entryFromCandidate);
  }, [person]);

  const [entries, setEntries] = useState<EntryForm[]>(initialEntries);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<{ field: string; message: string } | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const draggedIndexRef = useRef<number | null>(null);
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const modalTrapRef = useRef<HTMLDivElement>(null);
  useFocusTrap(modalTrapRef, true);
  useEffect(
    () => () => {
      if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
    },
    []
  );

  const parsedError = useMemo(() => parseErrorField(fieldError?.field), [fieldError]);

  const isDirty = useMemo(() => {
    if (entries.length !== initialEntries.length) return true;
    return (
      JSON.stringify(entries.map(entryToPayload)) !==
      JSON.stringify(initialEntries.map(entryToPayload))
    );
  }, [entries, initialEntries]);

  const confirmClose = useCallback(
    (next: () => void) => {
      if (!isDirty) {
        next();
        return;
      }
      const ok = window.confirm(
        'You have unsaved certification changes. Discard them and close the editor?'
      );
      if (ok) next();
    },
    [isDirty]
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') confirmClose(onClose);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [confirmClose, onClose]);

  const patchEntry = (idx: number, mut: (e: EntryForm) => EntryForm) => {
    setEntries((prev) => prev.map((e, i) => (i === idx ? mut(e) : e)));
    setFieldError(null);
  };

  const addEntry = () => setEntries((prev) => [...prev, blankEntry()]);

  const removeEntry = (idx: number) => {
    setEntries((prev) => prev.filter((_, i) => i !== idx));
    setFieldError(null);
  };

  const moveEntry = (from: number, to: number) => {
    if (from === to) return;
    setEntries((prev) => {
      const copy = prev.slice();
      const [item] = copy.splice(from, 1);
      copy.splice(to, 0, item);
      return copy;
    });
  };

  const onDragStart = (idx: number) => (e: React.DragEvent<HTMLLIElement>) => {
    draggedIndexRef.current = idx;
    e.dataTransfer.setData(DRAG_TYPE, String(idx));
    e.dataTransfer.effectAllowed = 'move';
  };
  const onDragOver = (idx: number) => (e: React.DragEvent<HTMLLIElement>) => {
    if (
      !Array.from(e.dataTransfer.types).includes(DRAG_TYPE) &&
      draggedIndexRef.current == null
    )
      return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(idx);
  };
  const onDragLeave = () => setDragOverIndex(null);
  const onDrop = (idx: number) => (e: React.DragEvent<HTMLLIElement>) => {
    e.preventDefault();
    const from = draggedIndexRef.current;
    setDragOverIndex(null);
    draggedIndexRef.current = null;
    if (from == null) return;
    moveEntry(from, idx);
  };
  const onDragEnd = () => {
    draggedIndexRef.current = null;
    setDragOverIndex(null);
  };

  const handleSave = async () => {
    if (!person.id || saving) return;
    // Client-side: name is required. Short-circuit before hitting the API
    // so the user gets an inline error on the right row.
    const missingNameIdx = entries.findIndex((e) => e.name.trim() === '');
    if (missingNameIdx !== -1) {
      setFieldError({
        field: `certifications[${missingNameIdx}].name`,
        message: `certifications[${missingNameIdx}].name is required`,
      });
      if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
      scrollTimerRef.current = setTimeout(() => {
        const el = document.querySelector(
          `[data-testid="certifications-editor-row-${missingNameIdx}"]`
        );
        if (el && typeof (el as HTMLElement).scrollIntoView === 'function') {
          (el as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 50);
      return;
    }

    setSaving(true);
    setServerError(null);
    setFieldError(null);
    try {
      const payload = entries.map(entryToPayload);
      const updated = await apiClient.replaceCertifications(person.id, payload);
      onSaved(updated);
    } catch (e: any) {
      const data = e?.response?.data ?? {};
      const message = data.error || e?.message || 'Failed to save certifications';
      if (typeof data.field === 'string') {
        setFieldError({ field: data.field, message });
        const parsed = parseErrorField(data.field);
        if (parsed?.index != null) {
          if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
          scrollTimerRef.current = setTimeout(() => {
            const el = document.querySelector(
              `[data-testid="certifications-editor-row-${parsed.index}"]`
            );
            if (el && typeof (el as HTMLElement).scrollIntoView === 'function') {
              (el as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 50);
        }
      } else {
        setServerError(message);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      ref={modalTrapRef}
      className="fixed inset-0 z-[70] flex flex-col bg-dark-bg"
      data-testid="certifications-editor-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="certifications-editor-title"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Sticky top bar */}
      <div className="flex items-center justify-between gap-4 p-4 border-b border-dark-border bg-dark-card">
        <div className="min-w-0">
          <h2 id="certifications-editor-title" className="text-lg font-bold text-dark-text">
            Edit certifications — {person.Name || 'Unknown'}
          </h2>
          <p className="text-xs text-dark-text-secondary mt-0.5">
            {entries.length} {entries.length === 1 ? 'entry' : 'entries'} · stored as{' '}
            <code className="text-amber-200">admin_edit</code> (CV re-imports won't
            overwrite). Drag rows to reorder.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => confirmClose(onClose)}
            disabled={saving}
            className="px-4 py-2 rounded-md bg-dark-bg border border-dark-border text-dark-text-secondary hover:text-dark-text disabled:opacity-50"
            data-testid="certifications-editor-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !isDirty}
            className="px-4 py-2 rounded-md bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="certifications-editor-save"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-6">
        {serverError && (
          <p
            className="mb-4 text-sm text-red-400"
            role="alert"
            data-testid="certifications-editor-error"
          >
            {serverError}
          </p>
        )}
        {fieldError && parsedError?.index == null && (
          <p
            className="mb-4 text-sm text-red-400"
            role="alert"
            data-testid="certifications-editor-field-error-global"
          >
            {fieldError.message}
          </p>
        )}

        {entries.length === 0 && (
          <div
            className="text-center py-12 text-dark-text-secondary text-sm border border-dashed border-dark-border rounded-lg"
            data-testid="certifications-editor-empty"
          >
            No certifications yet. Click "Add entry" to create one.
          </div>
        )}

        <ol className="space-y-4">
          {entries.map((entry, idx) => {
            const rowError =
              parsedError?.index === idx ? fieldError?.message ?? null : null;
            const isDragOver = dragOverIndex === idx;
            return (
              <li
                key={entry.localId}
                draggable
                onDragStart={onDragStart(idx)}
                onDragOver={onDragOver(idx)}
                onDragLeave={onDragLeave}
                onDrop={onDrop(idx)}
                onDragEnd={onDragEnd}
                className={`bg-dark-card border rounded-lg p-4 ${
                  rowError
                    ? 'border-red-500'
                    : isDragOver
                    ? 'border-indigo-400'
                    : 'border-dark-border'
                }`}
                data-testid={`certifications-editor-row-${idx}`}
              >
                <div className="flex items-center justify-between mb-3 gap-2">
                  <span
                    className="text-xs text-dark-text-secondary cursor-grab select-none"
                    aria-hidden="true"
                  >
                    ⠿ drag
                  </span>
                  <button
                    type="button"
                    onClick={() => removeEntry(idx)}
                    className="text-sm text-red-400 hover:text-red-300"
                    data-testid={`certifications-editor-row-${idx}-remove`}
                    aria-label="remove certification"
                  >
                    × Remove
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field
                    label="Name (required)"
                    testid={`certifications-editor-row-${idx}-name`}
                    error={parsedError?.leaf === 'name' ? rowError : null}
                  >
                    <input
                      type="text"
                      value={entry.name}
                      onChange={(e) =>
                        patchEntry(idx, (p) => ({ ...p, name: e.target.value }))
                      }
                      className="w-full px-2 py-1 text-sm rounded bg-dark-bg border border-dark-border text-dark-text"
                    />
                  </Field>
                  <Field
                    label="Issuer"
                    testid={`certifications-editor-row-${idx}-issuer`}
                    error={parsedError?.leaf === 'issuer' ? rowError : null}
                  >
                    <input
                      type="text"
                      value={entry.issuer}
                      onChange={(e) =>
                        patchEntry(idx, (p) => ({ ...p, issuer: e.target.value }))
                      }
                      className="w-full px-2 py-1 text-sm rounded bg-dark-bg border border-dark-border text-dark-text"
                    />
                  </Field>
                  <Field
                    label="Issued date (YYYY-MM-DD)"
                    testid={`certifications-editor-row-${idx}-issued_date`}
                    error={parsedError?.leaf === 'issued_date' ? rowError : null}
                  >
                    <input
                      type="text"
                      value={entry.issued_date}
                      onChange={(e) =>
                        patchEntry(idx, (p) => ({ ...p, issued_date: e.target.value }))
                      }
                      placeholder="2023-06-15"
                      className="w-full px-2 py-1 text-sm rounded bg-dark-bg border border-dark-border text-dark-text"
                    />
                  </Field>
                  <Field
                    label="Expires date (YYYY-MM-DD)"
                    testid={`certifications-editor-row-${idx}-expires_date`}
                    error={parsedError?.leaf === 'expires_date' ? rowError : null}
                  >
                    <input
                      type="text"
                      value={entry.expires_date}
                      onChange={(e) =>
                        patchEntry(idx, (p) => ({ ...p, expires_date: e.target.value }))
                      }
                      placeholder="2026-06-14"
                      className="w-full px-2 py-1 text-sm rounded bg-dark-bg border border-dark-border text-dark-text"
                    />
                  </Field>
                  <Field
                    label="Credential ID"
                    testid={`certifications-editor-row-${idx}-credential_id`}
                    error={parsedError?.leaf === 'credential_id' ? rowError : null}
                  >
                    <input
                      type="text"
                      value={entry.credential_id}
                      onChange={(e) =>
                        patchEntry(idx, (p) => ({ ...p, credential_id: e.target.value }))
                      }
                      className="w-full px-2 py-1 text-sm rounded bg-dark-bg border border-dark-border text-dark-text"
                    />
                  </Field>
                  <Field
                    label="Credential URL (https://…)"
                    testid={`certifications-editor-row-${idx}-credential_url`}
                    error={parsedError?.leaf === 'credential_url' ? rowError : null}
                  >
                    <input
                      type="text"
                      value={entry.credential_url}
                      onChange={(e) =>
                        patchEntry(idx, (p) => ({ ...p, credential_url: e.target.value }))
                      }
                      placeholder="https://…"
                      className="w-full px-2 py-1 text-sm rounded bg-dark-bg border border-dark-border text-dark-text"
                    />
                  </Field>
                </div>
              </li>
            );
          })}
        </ol>

        <button
          type="button"
          onClick={addEntry}
          className="mt-4 px-3 py-1 text-sm rounded bg-dark-bg border border-dashed border-dark-border text-dark-text-secondary hover:text-dark-text"
          data-testid="certifications-editor-add"
        >
          + Add entry
        </button>
      </div>
    </div>
  );
};

const Field: React.FC<{
  label: string;
  testid: string;
  error: string | null | undefined;
  children: React.ReactNode;
}> = ({ label, testid, error, children }) => (
  <div data-testid={`${testid}-wrap`}>
    <label className="block text-[11px] font-bold uppercase tracking-wide text-dark-text-secondary mb-1">
      {label}
    </label>
    {children}
    {error && (
      <p
        className="mt-1 text-xs text-red-400"
        role="alert"
        data-testid={`${testid}-error`}
      >
        {error}
      </p>
    )}
  </div>
);
